import { useSuspenseQuery } from "@tanstack/react-query";
import { createRoute, type AnyRoute } from "@tanstack/react-router";

import { loadView, publicViewQuery } from "@/api/views";
import { brandMeta, robotsMeta, ROOT_TITLE, titleWith, type Metadata } from "@/lib/head";
import DemoPage, { metadata as demoMetadata } from "@/pages/demo/page";
import DemoProjectPage, { metadata as demoProjectMetadata } from "@/pages/demo/project/page";
import DemoQuotePage, { metadata as demoQuoteMetadata } from "@/pages/demo/quote/page";
import DemoStatementPage, { metadata as demoStatementMetadata } from "@/pages/demo/statement/page";
import LandingPage, { metadata as landingMetadata } from "@/pages/page";
import PricingPage, { metadata as pricingMetadata } from "@/pages/pricing/page";
import PrivacyPage, { metadata as privacyMetadata } from "@/pages/privacy/page";
import TermsPage, { metadata as termsMetadata } from "@/pages/terms/page";
import ContractFormPage, { generateMetadata as contractFormMetadata } from "@/pages/tools/forms/contract/page";
import QuoteFormPage, { metadata as quoteFormMetadata } from "@/pages/tools/forms/quote/page";
import NoticeToolPage, { metadata as noticeMetadata } from "@/pages/tools/notice/page";
import OhouseCostPage, { metadata as ohouseMetadata } from "@/pages/tools/ohouse-cost/page";
import ToolsPage, { metadata as toolsMetadata } from "@/pages/tools/page";

import { rootRoute } from "./root-route";
import { demoRoute, toolsRoute } from "./w0b";

// legacy opengraph-image·twitter-image(1200x630 PNG)를 public 에 굳힌 것 — index.html 이 한 벌 싣고 프리렌더·vite dev 가 절대 주소로 바꾼다.
export const OG_IMAGE = { url: "/opengraph-image.png", alt: "buildpy — 인테리어 시공사 현장관리", type: "image/png", width: "1200", height: "630" } as const;

// 화면마다 같은 og·twitter 이미지 줄 — route head 가 아니라 index.html 에만 있다(tests/index-html.test.ts 가 맞춰 본다).
export const OG_META = [
  { property: "og:image", content: OG_IMAGE.url },
  { property: "og:image:alt", content: OG_IMAGE.alt },
  { property: "og:image:type", content: OG_IMAGE.type },
  { property: "og:image:width", content: OG_IMAGE.width },
  { property: "og:image:height", content: OG_IMAGE.height },
  { name: "twitter:card", content: "summary_large_image" },
  { name: "twitter:image", content: OG_IMAGE.url },
  { name: "twitter:image:alt", content: OG_IMAGE.alt },
  { name: "twitter:image:type", content: OG_IMAGE.type },
  { name: "twitter:image:width", content: OG_IMAGE.width },
  { name: "twitter:image:height", content: OG_IMAGE.height },
];

// legacy app/manifest.ts 대응 — public/manifest.webmanifest 를 index.html 이 한 벌 싣는다.
export const MANIFEST_LINK = { rel: "manifest", href: "/manifest.webmanifest" };

const titleOf = (title: Metadata["title"]): string => (typeof title === "object" && title !== null ? title.absolute : titleWith(ROOT_TITLE, title));

// 화면 metadata → route head — 제목·설명(없으면 루트 설명)과 그 og·twitter 제목·설명 · 화면마다 같은 이미지·매니페스트는 index.html 이 싣는다.
export const pageHead = (metadata: Metadata) => ({
  meta: [...brandMeta(titleOf(metadata.title), metadata.description), ...robotsMeta(metadata.robots)],
});

// 쿼리는 legacy searchParams 처럼 문자열·문자열 배열만 받는다.
const rawOf = (value: unknown): string | string[] | undefined =>
  typeof value === "string" ? value : Array.isArray(value) && value.every((item) => typeof item === "string") ? value : undefined;

export const landingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  head: () => pageHead(landingMetadata),
  component: LandingPage,
});

export const pricingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/pricing",
  head: () => pageHead(pricingMetadata),
  component: PricingPage,
});

export const termsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/terms",
  head: () => pageHead(termsMetadata),
  component: TermsPage,
});

export const privacyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/privacy",
  head: () => pageHead(privacyMetadata),
  component: PrivacyPage,
});

export const toolsIndexRoute = createRoute({
  getParentRoute: () => toolsRoute,
  path: "/",
  head: () => pageHead(toolsMetadata),
  component: ToolsPage,
});

interface ContractFormSearch {
  form?: string;
}

const contractFormQuery = (search: ContractFormSearch) => publicViewQuery("tools", ["contract-form"], search);

const ContractFormRouteComponent = () => {
  const search = contractFormRoute.useSearch();
  const { data } = useSuspenseQuery(contractFormQuery(search));
  return <ContractFormPage data={data} />;
};

export const contractFormRoute = createRoute({
  getParentRoute: () => toolsRoute,
  path: "/forms/contract",
  validateSearch: (search: Record<string, unknown>): ContractFormSearch => ({ form: typeof search.form === "string" ? search.form : undefined }),
  loaderDeps: ({ search }) => search,
  loader: ({ context, deps }) => loadView(context.queryClient, contractFormQuery(deps)),
  // 제목은 읽어 둔 양식 종류 — 못 읽었으면 기본(공정위) 양식.
  head: ({ match }) => {
    const form = match.context.queryClient.getQueryData(contractFormQuery(match.search).queryKey)?.form ?? "FTC_10079";
    return pageHead(contractFormMetadata(form));
  },
  component: ContractFormRouteComponent,
});

export const quoteFormRoute = createRoute({
  getParentRoute: () => toolsRoute,
  path: "/forms/quote",
  head: () => pageHead(quoteFormMetadata),
  component: QuoteFormPage,
});

export const noticeToolRoute = createRoute({
  getParentRoute: () => toolsRoute,
  path: "/notice",
  head: () => pageHead(noticeMetadata),
  component: NoticeToolPage,
});

export const ohouseCostRoute = createRoute({
  getParentRoute: () => toolsRoute,
  path: "/ohouse-cost",
  head: () => pageHead(ohouseMetadata),
  component: OhouseCostPage,
});

export const demoIndexRoute = createRoute({
  getParentRoute: () => demoRoute,
  path: "/",
  head: () => pageHead(demoMetadata),
  component: DemoPage,
});

export const demoProjectRoute = createRoute({
  getParentRoute: () => demoRoute,
  path: "/project",
  head: () => pageHead(demoProjectMetadata),
  component: DemoProjectPage,
});

interface DemoQuoteSearch {
  t?: string | string[];
}

const DemoQuoteRouteComponent = () => <DemoQuotePage searchParams={demoQuoteRoute.useSearch()} />;

export const demoQuoteRoute = createRoute({
  getParentRoute: () => demoRoute,
  path: "/quote",
  validateSearch: (search: Record<string, unknown>): DemoQuoteSearch => ({ t: rawOf(search.t) }),
  head: () => pageHead(demoQuoteMetadata),
  component: DemoQuoteRouteComponent,
});

interface DemoStatementSearch {
  by?: string | string[];
}

const DemoStatementRouteComponent = () => <DemoStatementPage searchParams={demoStatementRoute.useSearch()} />;

export const demoStatementRoute = createRoute({
  getParentRoute: () => demoRoute,
  path: "/statement",
  validateSearch: (search: Record<string, unknown>): DemoStatementSearch => ({ by: rawOf(search.by) }),
  head: () => pageHead(demoStatementMetadata),
  component: DemoStatementRouteComponent,
});

// g4c 배치의 라우트 — 첫 화면 · 요금제·약관 3 · 무료 도구 5 · 고객 화면 데모 4.
export const routes: AnyRoute[] = [
  landingRoute,
  pricingRoute,
  termsRoute,
  privacyRoute,
  toolsIndexRoute,
  contractFormRoute,
  quoteFormRoute,
  noticeToolRoute,
  ohouseCostRoute,
  demoIndexRoute,
  demoProjectRoute,
  demoQuoteRoute,
  demoStatementRoute,
];
